import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Play } from "lucide-react";
import { BRAND, NAV_LINKS } from "../data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.nav
      data-testid="main-navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-strong py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 flex items-center justify-between">
        <a
          href="#home"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={BRAND.logo}
            alt="it's Malie"
            className="w-10 h-10 rounded-full border border-white/10 object-cover"
            loading="eager"
          />
          <span className="font-display text-lg tracking-tight">it&apos;s Malie</span>
        </a>

        <ul className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <a
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                href={l.href}
                className="link-underline text-[0.78rem] uppercase tracking-[0.22em] text-white/80 hover:text-white"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            href="#booking"
            data-testid="nav-book-artist-cta"
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[0.72rem] uppercase tracking-[0.22em] hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors"
          >
            Book Artist
            <Play size={14} strokeWidth={1.5} />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden w-11 h-11 grid place-items-center rounded-full border border-white/15"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden glass-strong border-t border-white/5 mt-3"
          >
            <ul className="flex flex-col px-6 py-6 gap-4">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="text-sm uppercase tracking-[0.22em] text-white/85"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#booking"
                  data-testid="mobile-book-artist-cta"
                  onClick={() => setOpen(false)}
                  className="btn-gold mt-2 w-full justify-center"
                >
                  Book Artist
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
